import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import './HomePageCMS.css';
import '../../styles/AdminShared.css';

const API = process.env.REACT_APP_API_URL || '/api';

interface DeliveryRow { location: string; location_bn: string; time: string; time_bn: string; notes: string; notes_bn: string; }
interface CostCard { icon: string; title: string; title_bn: string; description: string; description_bn: string; }

interface ShippingContent {
  hero_title: string; hero_title_bn: string;
  hero_subtitle: string; hero_subtitle_bn: string;
  overview_title: string; overview_title_bn: string;
  overview_content: string; overview_content_bn: string;
  overview_note: string; overview_note_bn: string;
  delivery_table: DeliveryRow[];
  delivery_title: string; delivery_title_bn: string;
  costs_title: string; costs_title_bn: string;
  costs_content: string; costs_content_bn: string;
  costs_cards: CostCard[];
  tracking_title: string; tracking_title_bn: string;
  tracking_content: string; tracking_content_bn: string;
  issues_title: string; issues_title_bn: string;
  issues_content: string; issues_content_bn: string;
}

const EMPTY: ShippingContent = {
  hero_title: '', hero_title_bn: '', hero_subtitle: '', hero_subtitle_bn: '',
  overview_title: '', overview_title_bn: '', overview_content: '', overview_content_bn: '',
  overview_note: '', overview_note_bn: '',
  delivery_table: [], delivery_title: '', delivery_title_bn: '',
  costs_title: '', costs_title_bn: '', costs_content: '', costs_content_bn: '', costs_cards: [],
  tracking_title: '', tracking_title_bn: '', tracking_content: '', tracking_content_bn: '',
  issues_title: '', issues_title_bn: '', issues_content: '', issues_content_bn: '',
};

const ShippingPageCMS: React.FC = () => {
  const [content, setContent] = useState<ShippingContent>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/cms/shipping/`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setContent(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setContent(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  /* Delivery table helpers */
  const addDeliveryRow = () => setContent(prev => ({
    ...prev, delivery_table: [...prev.delivery_table, { location: '', location_bn: '', time: '', time_bn: '', notes: '', notes_bn: '' }],
  }));
  const removeDeliveryRow = (i: number) => setContent(prev => ({
    ...prev, delivery_table: prev.delivery_table.filter((_, idx) => idx !== i),
  }));
  const updateDeliveryRow = (i: number, field: keyof DeliveryRow, value: string) => {
    const rows = [...content.delivery_table];
    rows[i] = { ...rows[i], [field]: value };
    setContent(prev => ({ ...prev, delivery_table: rows }));
  };

  /* Cost card helpers */
  const addCostCard = () => setContent(prev => ({
    ...prev, costs_cards: [...prev.costs_cards, { icon: '\uD83D\uDCE6', title: '', title_bn: '', description: '', description_bn: '' }],
  }));
  const removeCostCard = (i: number) => setContent(prev => ({
    ...prev, costs_cards: prev.costs_cards.filter((_, idx) => idx !== i),
  }));
  const updateCostCard = (i: number, field: keyof CostCard, value: string) => {
    const cards = [...content.costs_cards];
    cards[i] = { ...cards[i], [field]: value };
    setContent(prev => ({ ...prev, costs_cards: cards }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/cms/shipping/1/`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(content),
      });
      if (res.ok) { setContent(await res.json()); Swal.fire({ icon: 'success', title: 'Saved!', text: 'Shipping page updated', timer: 1500, showConfirmButton: false }); }
      else throw new Error();
    } catch { Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save' }); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="homepage-cms-container"><div className="cms-card"><div className="card-content">
      <div className="cms-skeleton" style={{ height: 40 }} />
      <div className="cms-skeleton" style={{ height: 40, marginTop: 12 }} />
    </div></div></div>
  );

  return (
    <div className="homepage-cms-container">
      <form onSubmit={handleSubmit}>

        {/* Hero */}
        <div className="cms-card">
          <div className="card-header"><div className="card-header-left"><div className="card-icon icon-blue">{'\uD83D\uDE9A'}</div><h3>Hero Section</h3></div></div>
          <div className="card-content">
            <div className="cms-grid-2">
              <div className="cms-form-group"><label>Hero Title (English)</label><input className="form-input" name="hero_title" value={content.hero_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Hero Title (??)</label><input className="form-input" name="hero_title_bn" value={content.hero_title_bn} onChange={handleChange} /></div>
            </div>
            <div className="cms-grid-2" style={{ marginTop: 8 }}>
              <div className="cms-form-group"><label>Hero Subtitle (English)</label><input className="form-input" name="hero_subtitle" value={content.hero_subtitle} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Hero Subtitle (??)</label><input className="form-input" name="hero_subtitle_bn" value={content.hero_subtitle_bn} onChange={handleChange} /></div>
            </div>
          </div>
        </div>

        {/* Overview */}
        <div className="cms-card">
          <div className="card-header"><div className="card-header-left"><div className="card-icon icon-blue">{'\uD83D\uDCCB'}</div><h3>Shipping Overview</h3></div></div>
          <div className="card-content">
            <div className="cms-grid-2" style={{ marginBottom: 8 }}>
              <div className="cms-form-group"><label>Title (English)</label><input className="form-input" name="overview_title" value={content.overview_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Title (??)</label><input className="form-input" name="overview_title_bn" value={content.overview_title_bn} onChange={handleChange} /></div>
            </div>
            <div className="cms-grid-2" style={{ marginBottom: 8 }}>
              <div className="cms-form-group"><label>Content (English)</label><textarea className="form-input" name="overview_content" value={content.overview_content} onChange={handleChange} rows={4} style={{ resize: 'vertical' }} /></div>
              <div className="cms-form-group"><label>Content (??)</label><textarea className="form-input" name="overview_content_bn" value={content.overview_content_bn} onChange={handleChange} rows={4} style={{ resize: 'vertical' }} /></div>
            </div>
            <div className="cms-grid-2">
              <div className="cms-form-group"><label>Note (English)</label><input className="form-input" name="overview_note" value={content.overview_note} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Note (??)</label><input className="form-input" name="overview_note_bn" value={content.overview_note_bn} onChange={handleChange} /></div>
            </div>
          </div>
        </div>

        {/* Delivery table */}
        <div className="cms-card">
          <div className="card-header">
            <div className="card-header-left"><div className="card-icon icon-blue">{'\uD83D\uDCCA'}</div><h3>Delivery Timeframes</h3></div>
            <button type="button" className="btn-add-feature" onClick={addDeliveryRow}>+ Add Row</button>
          </div>
          <div className="card-content">
            <div className="cms-grid-2" style={{ marginBottom: 12 }}>
              <div className="cms-form-group"><label>Section Title (English)</label><input className="form-input" name="delivery_title" value={content.delivery_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Section Title (??)</label><input className="form-input" name="delivery_title_bn" value={content.delivery_title_bn} onChange={handleChange} /></div>
            </div>
            {content.delivery_table.length === 0 && <div className="empty-state"><p>No delivery rows yet.</p></div>}
            {content.delivery_table.map((row, i) => (
              <div key={i} className="feature-item-card" style={{ marginBottom: 10 }}>
                <div className="feature-header">
                  <span className="feature-count">#{i + 1}</span>
                  <button type="button" className="btn-remove-feature" onClick={() => removeDeliveryRow(i)}>{'\u2715'}</button>
                </div>
                <div className="cms-grid-2" style={{ marginBottom: 8 }}>
                  <div className="cms-form-group"><label>Location (English)</label><input className="form-input" value={row.location} onChange={e => updateDeliveryRow(i, 'location', e.target.value)} /></div>
                  <div className="cms-form-group"><label>Location (??)</label><input className="form-input" value={row.location_bn} onChange={e => updateDeliveryRow(i, 'location_bn', e.target.value)} /></div>
                </div>
                <div className="cms-grid-2" style={{ marginBottom: 8 }}>
                  <div className="cms-form-group"><label>Estimated Time (English)</label><input className="form-input" value={row.time} onChange={e => updateDeliveryRow(i, 'time', e.target.value)} /></div>
                  <div className="cms-form-group"><label>Estimated Time (??)</label><input className="form-input" value={row.time_bn} onChange={e => updateDeliveryRow(i, 'time_bn', e.target.value)} /></div>
                </div>
                <div className="cms-grid-2">
                  <div className="cms-form-group"><label>Notes (English)</label><input className="form-input" value={row.notes} onChange={e => updateDeliveryRow(i, 'notes', e.target.value)} /></div>
                  <div className="cms-form-group"><label>Notes (??)</label><input className="form-input" value={row.notes_bn} onChange={e => updateDeliveryRow(i, 'notes_bn', e.target.value)} /></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Costs */}
        <div className="cms-card">
          <div className="card-header">
            <div className="card-header-left"><div className="card-icon icon-blue">{'\uD83D\uDCB0'}</div><h3>Shipping Costs</h3></div>
            <button type="button" className="btn-add-feature" onClick={addCostCard}>+ Add Card</button>
          </div>
          <div className="card-content">
            <div className="cms-grid-2" style={{ marginBottom: 12 }}>
              <div className="cms-form-group"><label>Title (English)</label><input className="form-input" name="costs_title" value={content.costs_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Title (??)</label><input className="form-input" name="costs_title_bn" value={content.costs_title_bn} onChange={handleChange} /></div>
            </div>
            <div className="cms-grid-2" style={{ marginBottom: 12 }}>
              <div className="cms-form-group"><label>Content (English)</label><input className="form-input" name="costs_content" value={content.costs_content} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Content (??)</label><input className="form-input" name="costs_content_bn" value={content.costs_content_bn} onChange={handleChange} /></div>
            </div>
            {content.costs_cards.map((card, i) => (
              <div key={i} className="feature-item-card" style={{ marginBottom: 10 }}>
                <div className="feature-header">
                  <span className="feature-count">{card.icon}</span>
                  <button type="button" className="btn-remove-feature" onClick={() => removeCostCard(i)}>{'\u2715'}</button>
                </div>
                <div className="cms-form-group" style={{ marginBottom: 8 }}>
                  <label>Icon (emoji)</label>
                  <input className="form-input" value={card.icon} onChange={e => updateCostCard(i, 'icon', e.target.value)} style={{ maxWidth: 120 }} />
                </div>
                <div className="cms-grid-2" style={{ marginBottom: 8 }}>
                  <div className="cms-form-group"><label>Title (English)</label><input className="form-input" value={card.title} onChange={e => updateCostCard(i, 'title', e.target.value)} /></div>
                  <div className="cms-form-group"><label>Title (??)</label><input className="form-input" value={card.title_bn} onChange={e => updateCostCard(i, 'title_bn', e.target.value)} /></div>
                </div>
                <div className="cms-grid-2">
                  <div className="cms-form-group"><label>Description (English)</label><textarea className="form-input" value={card.description} onChange={e => updateCostCard(i, 'description', e.target.value)} rows={2} style={{ resize: 'vertical' }} /></div>
                  <div className="cms-form-group"><label>Description (??)</label><textarea className="form-input" value={card.description_bn} onChange={e => updateCostCard(i, 'description_bn', e.target.value)} rows={2} style={{ resize: 'vertical' }} /></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tracking */}
        <div className="cms-card">
          <div className="card-header"><div className="card-header-left"><div className="card-icon icon-blue">{'\uD83D\uDCCD'}</div><h3>Order Tracking</h3></div></div>
          <div className="card-content">
            <div className="cms-grid-2" style={{ marginBottom: 8 }}>
              <div className="cms-form-group"><label>Title (English)</label><input className="form-input" name="tracking_title" value={content.tracking_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Title (??)</label><input className="form-input" name="tracking_title_bn" value={content.tracking_title_bn} onChange={handleChange} /></div>
            </div>
            <div className="cms-grid-2">
              <div className="cms-form-group"><label>Content (English)</label><textarea className="form-input" name="tracking_content" value={content.tracking_content} onChange={handleChange} rows={4} style={{ resize: 'vertical' }} /></div>
              <div className="cms-form-group"><label>Content (??)</label><textarea className="form-input" name="tracking_content_bn" value={content.tracking_content_bn} onChange={handleChange} rows={4} style={{ resize: 'vertical' }} /></div>
            </div>
          </div>
        </div>

        {/* Issues */}
        <div className="cms-card">
          <div className="card-header"><div className="card-header-left"><div className="card-icon icon-blue">{'\u26A0\uFE0F'}</div><h3>Shipping Issues</h3></div></div>
          <div className="card-content">
            <div className="cms-grid-2" style={{ marginBottom: 8 }}>
              <div className="cms-form-group"><label>Title (English)</label><input className="form-input" name="issues_title" value={content.issues_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Title (??)</label><input className="form-input" name="issues_title_bn" value={content.issues_title_bn} onChange={handleChange} /></div>
            </div>
            <div className="cms-grid-2">
              <div className="cms-form-group"><label>Content (English)</label><textarea className="form-input" name="issues_content" value={content.issues_content} onChange={handleChange} rows={4} style={{ resize: 'vertical' }} /></div>
              <div className="cms-form-group"><label>Content (??)</label><textarea className="form-input" name="issues_content_bn" value={content.issues_content_bn} onChange={handleChange} rows={4} style={{ resize: 'vertical' }} /></div>
            </div>
          </div>
        </div>

        <div className="cms-page-actions">
          <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving...' : '\uD83D\uDCBE Save Changes'}</button>
        </div>
      </form>
    </div>
  );
};

export default ShippingPageCMS;
